/**
 * Helper class for canvas operations.
 */
export class CanvasHelper {
  /**
   * Combines multiple canvas sources into a single PNG image on a target canvas.
   *
   * @param config.target - The target canvas element to draw onto.
   * @param config.sources - An array of source canvas elements to layer.
   * @returns A promise that resolves to a PNG data URL of the combined image.
   */
  static async combineToPNG(config: {
    target: HTMLCanvasElement;
    sources: HTMLCanvasElement[];
  }): Promise<string> {
    // Get the 2D rendering context from the target canvas
    const ctx = config.target.getContext('2d');

    // If context is not available, return an empty string
    if (!ctx) {
      return '';
    }

    // Clear the target canvas to remove any previous drawings
    ctx.clearRect(0, 0, config.target.width, config.target.height);

    // Convert each source canvas to a PNG data URL
    const dataUrls = config.sources.map((c) => c.toDataURL('image/png'));

    // Create HTMLImageElements from the data URLs and wait for all to load
    const images = (await Promise.all(
      dataUrls.map((d) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = d;
          img.onload = () => resolve(img);
        });
      })
    )) as HTMLImageElement[];

    // Draw each image onto the target canvas, layering them in order
    images.forEach((img) => ctx.drawImage(img, 0, 0));

    // Export the combined result as a PNG data URL
    const output = config.target.toDataURL('image/png');
    return output;
  }

  static getDotStyle = (
    rgb: string, // what color to use if not secondary
    isSecondary: boolean
  ): {
    rgb: string;
    fillOpacity: string;
    lineOpacity: string;
  } => {
    if (isSecondary) {
      return {
        rgb: '100,100,100',
        fillOpacity: '50%',
        lineOpacity: '75%',
      };
    }
    return {
      rgb: rgb,
      fillOpacity: '75%',
      lineOpacity: '100%',
    };
  };
}
